import { useRef, useCallback, useState, useMemo, useEffect } from "react";
import lottie from "lottie-web";
import { useThrottleFn } from "ahooks";
import { BlandWebClient } from "bland-client-js-sdk";
import { XIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { MicStopIcon, MicIcon, VoiceIcon } from "@/components/Icons";
import api from "@/lib/api/client";
import { cn } from "@/lib/utils";
import voiceAnimationData from "./voice-animation.json";
import { ChatMode } from "@/stores/page";
import {
  type VoiceSwitchProps,
  VoiceSwitch
} from "@/components/VoiceAgentSetting";

export enum VoiceStatus {
  Stop = "stop",
  Stopping = "stopping",
  Connecting = "connecting",
  Thinking = "thinking",
  Silence = "silence",
  Talking = "talking"
}

// 语音录制相关的全局变量
let audioStream: MediaStream | null = null;
let audioContext: AudioContext | null = null;
let analyser: AnalyserNode | null = null;
let dataArray: Uint8Array | null = null;
let animationFrameId: number | null = null;
let blandClientRef: BlandWebClient | null = null;

const BASE_AMPLITUDE = 0.1;

// 格式化时间为 MM:SS 格式
const formatTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
};

export default function VoiceChatBox({
  id,
  mode,
  isMobile,
  preview,
  status,
  setVoiceStatus,
  maximum_session_time,
  onReason,
  inVoice
}: {
  id: string;
  mode: ChatMode;
  isMobile: boolean;
  preview: boolean;
  status: VoiceStatus;
  setVoiceStatus: (status: VoiceStatus) => void;
  maximum_session_time?: number;
  onReason: (reason: string) => void;
  inVoice?: VoiceSwitchProps;
}) {
  const { t } = useTranslation("translations");
  const [audioLevel, setAudioLevel] = useState(0);
  const [voiceTimer, setVoiceTimer] = useState<number>(0);
  const [isSaying, setIsSaying] = useState(false);
  const remainingVoiceTime = useMemo(
    () => (maximum_session_time || 300) - voiceTimer,
    [maximum_session_time, voiceTimer]
  );

  const isRecording = useMemo(() => status !== VoiceStatus.Stop, [status]);
  const voiceTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const voiceAnimationRef = useRef<ReturnType<
    typeof lottie.loadAnimation
  > | null>(null);

  const initVoiceAnimation = useCallback(() => {
    voiceAnimationRef.current = lottie.loadAnimation({
      container: document.getElementById("voice-animation") as Element,
      renderer: "svg",
      loop: false,
      autoplay: false,
      animationData: voiceAnimationData
    });
  }, []);

  useEffect(() => {
    initVoiceAnimation();
    return () => {
      voiceAnimationRef.current?.stop();
      voiceAnimationRef.current?.destroy();
      voiceAnimationRef.current = null;
    };
  }, []);

  useEffect(() => {
    console.log("voice status:", status);
    let isComplete = false;

    voiceAnimationRef.current?.playSegments([50, 140], true);
    voiceAnimationRef.current?.play();
    voiceAnimationRef.current?.addEventListener("complete", () => {
      isComplete = !isComplete;
      voiceAnimationRef.current?.playSegments(
        isComplete ? [140, 50] : [50, 140],
        true
      );
    });

    if (
      [VoiceStatus.Thinking, VoiceStatus.Silence, VoiceStatus.Talking].includes(
        status
      )
    ) {
      if (!voiceTimerRef.current && status === VoiceStatus.Talking) {
        voiceTimerRef.current = setInterval(() => {
          setVoiceTimer((pre) => pre + 1);
        }, 1000);
      }

      if (status === VoiceStatus.Talking) {
        setAudioLevel(0.5);
        // 解除静音
        toggleMuteAudioStream(false);
      } else if (status === VoiceStatus.Silence) {
        setAudioLevel(0);
        // 全局静音麦克风
        toggleMuteAudioStream(true);
      }
    } else {
      if (voiceTimerRef.current) {
        clearInterval(voiceTimerRef.current);
        voiceTimerRef.current = null;
      }
    }
    if (status === VoiceStatus.Stopping) {
      cleanupVoiceRecording();
    }
    return () => {
      if (voiceTimerRef.current) {
        clearInterval(voiceTimerRef.current);
        voiceTimerRef.current = null;
      }
    };
  }, [status]);

  const getToken = useCallback(async () => {
    const res = await api.mapping.v1.agents[":id"].voice.session
      .$post({
        param: {
          id
        },
        // @ts-ignore
        json: {
          env: preview ? "preview" : "live"
        }
      })
      .then(
        (res) =>
          res.json() as Promise<{
            session_token: string;
            agent_id: string;
            error: string;
          }>
      );
    return res;
  }, []);

  // 根据音频数据计算振幅值
  const calculateAmplitudeFromAudioBase = useCallback(
    (audioData: Uint8Array) => {
      if (!audioData || audioData.length === 0) return BASE_AMPLITUDE;

      // 计算平均音量
      let sum = 0;
      for (let i = 0; i < audioData.length; i++) {
        sum += audioData[i];
      }
      const average = sum / audioData.length;

      // 标准化到合适的振幅范围 (0.1-1.0)
      const normalizedLevel = Math.min(average / 128, 1);
      const amplitude = Math.max(BASE_AMPLITUDE, normalizedLevel * 1);

      // console.log("bland audio amplitude:", amplitude, "average:", average);

      return amplitude;
    },
    []
  );

  const { run: calculateAmplitudeFromAudio } = useThrottleFn(
    calculateAmplitudeFromAudioBase,
    { wait: 100 } // 减少到100ms来提高响应性
  );

  const initBlandClient = useCallback(async () => {
    try {
      const { session_token, error, agent_id } = await getToken();
      if (error) {
        setVoiceStatus(VoiceStatus.Stop);
        onReason(error);
        return;
      }

      if (!blandClientRef) {
        blandClientRef = new BlandWebClient(agent_id, session_token, {
          customEndpoint: "/api/ws/voice"
        });

        // 添加监听器
        blandClientRef.addListener("open", () => {
          console.info("client open");
        });
        blandClientRef.addListener("close", (code, reason) => {
          console.info("client close:", code, reason);
          onReason(reason);
        });
        blandClientRef.addListener("error", (event) => {
          console.info("client error:", event);
        });
        blandClientRef.addListener("agentStartTalking", () => {
          console.log("agentStartTalking");
          if ([VoiceStatus.Stop, VoiceStatus.Connecting].includes(status)) {
            setVoiceStatus(VoiceStatus.Talking);
          }
          setIsSaying(false);
        });
        blandClientRef.addListener("userStartTalking", () => {
          setIsSaying(true);
        });
        blandClientRef.addListener("conversationEnded", (e) => {
          console.log("conversationEnded: ", e);
          setVoiceStatus(VoiceStatus.Stop);
          if (e.reason) {
            onReason(e.reason);
          }
        });
        blandClientRef.addListener("audio", (audio) => {
          // console.log("🔄 Processing audio data...");
          const audioData = audio as Uint8Array;
          const amplitude = calculateAmplitudeFromAudio(audioData);
          if (
            [VoiceStatus.Stop, VoiceStatus.Connecting].includes(status) &&
            amplitude > BASE_AMPLITUDE
          ) {
            console.log("audio talking");
            setVoiceStatus(VoiceStatus.Talking);
          }
          // console.log("audioLevel", amplitude, isSaying);
          if (amplitude > BASE_AMPLITUDE && !isSaying) {
            setAudioLevel(amplitude);
          }
        });

        blandClientRef.addListener("error", (error) => {
          console.error("🚨 BlandWebClient error:", error);

          if (
            error &&
            typeof error === "object" &&
            (error.message?.includes("audioWorklet") ||
              error.message?.includes("AudioWorkletProcessor") ||
              error.name === "InvalidStateError")
          ) {
            console.warn(
              "⚠️ AudioWorklet conflict detected, attempting recovery..."
            );
          }
        });
      }

      blandClientRef.initConversation({
        callId: "",
        customStream: audioStream!,
        sampleRate: 44100
      });
    } catch (error) {
      console.error("❌ Error initializing BlandWebClient:", error);
      throw error;
    }
  }, [getToken, calculateAmplitudeFromAudioBase]);

  // 分析音频获取音量级别
  const analyzeAudio = () => {
    if (!analyser || !dataArray) return;

    analyser.getByteFrequencyData(dataArray);

    // 计算平均音量
    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      sum += dataArray[i];
    }
    const average = sum / dataArray.length;
    const normalizedLevel = Math.min(average / 128, 1); // 标准化到0-1

    if (isSaying) {
      // console.log("audioLevel isSaying:", normalizedLevel);
      setAudioLevel(normalizedLevel);
    }

    // console.log("audioLevel", normalizedLevel);

    if (isRecording) {
      animationFrameId = requestAnimationFrame(analyzeAudio);
    }
  };
  // 全局控制麦克风的静音状态
  // 通过禁用 MediaStream 的音频轨道来实现全局静音
  const toggleMuteAudioStream = useCallback((mute: boolean) => {
    if (audioStream) {
      const audioTracks = audioStream.getAudioTracks();
      audioTracks.forEach((track) => {
        console.log(`🎤 Microphone ${track.id} ${mute ? "muted" : "unmuted"}`);
        track.enabled = !mute; // false = 静音，true = 解除静音
      });
      console.log(`🎤 Microphone globally ${mute ? "muted" : "unmuted"}`);
    }
  }, []);

  // 清理语音录制资源
  const cleanupVoiceRecording = useCallback(() => {
    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }

    if (audioStream) {
      audioStream.getTracks().forEach((track) => track.stop());
      audioStream = null;
      console.log("✅ Audio stream stopped");
    }
    toggleMuteAudioStream(false);

    audioContext?.close();
    audioContext = null;

    blandClientRef?.stopConversation();
    blandClientRef = null;

    analyser = null;
    dataArray = null;
  }, []);

  // 开始语音录制
  const startVoiceRecording = async () => {
    try {
      console.log("🎤 Starting voice recording...");

      // 申请麦克风权限
      audioStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      });

      console.log("✅ Microphone access granted");

      const audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)();

      analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(audioStream);
      source.connect(analyser);

      analyser.fftSize = 256;
      const bufferLength = analyser.frequencyBinCount;
      dataArray = new Uint8Array(bufferLength);

      // 开始音频分析
      analyzeAudio();
      initBlandClient();
    } catch (error) {
      console.error("❌ Error starting voice recording:", error);

      // 更详细的错误信息
      if (error instanceof Error) {
        if (error.name === "NotAllowedError") {
          alert(t("voiceChat.errors.permissionDenied"));
        } else if (error.name === "NotFoundError") {
          alert(t("voiceChat.errors.deviceNotFound"));
        } else {
          alert(t("voiceChat.errors.accessFailed", { message: error.message }));
        }
      }

      cleanupVoiceRecording();
    }
  };

  // 监听倒计时结束，自动断开语音
  useEffect(() => {
    if (remainingVoiceTime <= 0 && status !== VoiceStatus.Stop) {
      setVoiceStatus(VoiceStatus.Stopping);
      setVoiceTimer(0);
    }
  }, [remainingVoiceTime, status]);

  useEffect(() => {
    if (!inVoice) {
      startVoiceRecording();
    }
    return () => {
      cleanupVoiceRecording();
    };
  }, [inVoice]);

  return (
    <div
      className={cn(
        "absolute z-40 rounded-xl bg-white",
        status === VoiceStatus.Stop && !inVoice && "hidden",
        inVoice
          ? "top-[70px] left-0 h-[calc(100vh-148px)] w-full"
          : mode === ChatMode.Widget || isMobile || preview
            ? "top-0 left-0 h-full w-full"
            : "top-9 left-9 shadow-[0px_0px_36px_0px_rgba(0,0,0,0.12)]"
      )}
      style={
        mode === ChatMode.Nav && !isMobile && !preview
          ? {
              width: "calc(100% - 72px)",
              height: "calc(100% - 46px)"
            }
          : undefined
      }
    >
      {inVoice && (
        <p
          className="mt-2 px-4 text-center text-sm font-normal text-slate-400"
          data-testid="chat-preview-notice"
        >
          {t("agentChat.previewNotice")}
        </p>
      )}

      <div
        className={cn(
          "mx-auto flex h-full w-full max-w-[520px] flex-col",
          inVoice && "mt-[-60px] justify-center gap-1"
        )}
      >
        <div
          className={cn(
            "flex w-full flex-1 items-center justify-center",
            inVoice && !inVoice.checked ? "hidden" : "flex"
          )}
        >
          <div
            id="voice-animation"
            className={cn(
              "h-[300px] w-[300px]",
              inVoice && !inVoice.checked
                ? "hidden"
                : status === VoiceStatus.Talking
                  ? "opacity-100"
                  : "opacity-50"
            )}
            style={{
              transition: "all 0.2s ease-in-out",
              transform: `scale(${status === VoiceStatus.Talking ? 0.6 + audioLevel * 0.4 : "0.6"})`
            }}
          ></div>
        </div>

        {inVoice && !inVoice.checked && (
          <>
            <img
              className="mx-auto h-auto w-[180px]"
              src="/images/voice-disabled.svg"
            />

            <p className="mt-4 text-center text-base font-semibold text-black">
              {t("voiceChat.disable.title")}
            </p>
            <p className="justify-center px-4 text-center text-sm font-normal text-[#5c5c5c]">
              {t("voiceChat.disable.description")}
            </p>
            <div className="mx-auto mt-2 inline-flex">
              <VoiceSwitch
                isLoading={inVoice.isLoading}
                disabled={inVoice.disabled}
                checked={inVoice.checked}
                onCheckedChange={inVoice.onCheckedChange}
              />
            </div>
          </>
        )}

        {inVoice && inVoice.checked && status === VoiceStatus.Stop && (
          <div
            className={cn(
              "flex-col items-center justify-center gap-2.5 pb-10",
              status === VoiceStatus.Stop ? "flex" : "hidden"
            )}
          >
            <div
              className="inline-flex h-16 w-16 cursor-pointer items-center justify-center overflow-hidden rounded-full bg-[#007af5]"
              onClick={() => {
                setVoiceStatus(VoiceStatus.Connecting);
                startVoiceRecording();
              }}
            >
              <VoiceIcon className="h-5 w-5 text-white" />
            </div>
            <div className="text-center text-xs font-normal text-[#a0aec0]">
              {t("voiceChat.testCall")}
            </div>
          </div>
        )}

        <div
          className={cn(
            "mt-auto items-center justify-between px-8 pb-10",
            status === VoiceStatus.Stop ? "hidden" : "flex"
          )}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className={cn(
                  "inline-flex h-16 w-16 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-full",
                  status === VoiceStatus.Silence
                    ? "bg-[#fff7f7] hover:bg-[#ffebeb]"
                    : "bg-[#eef4ff] hover:bg-[#deeaff]"
                )}
                onClick={() =>
                  setVoiceStatus(
                    status === VoiceStatus.Silence
                      ? VoiceStatus.Talking
                      : VoiceStatus.Silence
                  )
                }
              >
                {status === VoiceStatus.Silence ? (
                  <MicStopIcon className="h-5 w-5 text-[#e53e3e]" />
                ) : (
                  <MicIcon className={cn("h-5 w-5 text-black")} />
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                {status === VoiceStatus.Silence
                  ? t("voiceChat.openMic")
                  : t("voiceChat.muteMic")}
              </p>
            </TooltipContent>
          </Tooltip>
          <div className="text-center text-base font-medium text-[#403e3e]">
            {status === VoiceStatus.Connecting
              ? t("voiceChat.connecting")
              : status === VoiceStatus.Stopping
                ? t("voiceChat.stopping")
                : `${formatTime(remainingVoiceTime)}`}
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className="inline-flex h-16 w-16 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-full bg-[#eef4ff] hover:bg-[#deeaff]"
                onClick={() => {
                  setVoiceStatus(VoiceStatus.Stopping);
                  setVoiceTimer(0);
                }}
              >
                <XIcon className="h-5 w-5 text-black" />
              </div>
            </TooltipTrigger>
            <TooltipContent>{t("voiceChat.endChat")}</TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}
